import React, { createContext, useContext, useEffect, useMemo } from "react";

import { useLocalStorage } from "../hooks";

export type Tenant = string;

export interface Tenancy {
  setTenant: (tenant: Tenant) => Tenant;
}

export interface TenancyContextValue {
  tenant?: Tenant | null;
  setTenant: (tenant: Tenant) => void;
}

export const TenancyContext = createContext<TenancyContextValue>({
  tenant: null,
  setTenant: () => {},
});

interface TenancyProviderProps {
  tenant?: Tenant | null;
  onChange?: (tenant: string) => void;
  localStorageKey?: string;
  children: React.ReactNode;
}

export function TenancyProvider(props: TenancyProviderProps) {
  const {
    children,
    tenant,
    onChange,
    localStorageKey = "saas-ui.tenant",
  } = props;

  const [_tenant, setTenant] = useLocalStorage(localStorageKey, tenant);

  useEffect(() => {
    if (tenant && tenant !== _tenant) {
      setTenant(tenant);
    }
  }, [tenant]);

  const context = useMemo(
    () => ({
      tenant: _tenant,
      setTenant: (tenant: Tenant) => {
        setTenant(tenant);
        onChange?.(tenant);
      },
    }),
    [_tenant, onChange],
  );

  return (
    <TenancyContext.Provider value={context}>
      {children}
    </TenancyContext.Provider>
  );
}

export const useTenant = () => {
  const context = useContext(TenancyContext);
  if (context === undefined) {
    throw new Error("useTenant must be used within a TenancyProvider");
  }
  return context;
};
